import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import cn from "classnames";
import { ValidityDateRangeFields } from "components/ui/DayPicker";
import { Select } from "components/ui/Select";
import { CARRIER_SELECT_OPTIONS, CARRIER_TBN_NAME, resolveCarrier } from "constants/carriers.catalog";
import { streamRatesImport, streamRatesReply } from "../../api/rates.stream";
import type { RateImportSession } from "../../api/rates.types";
import { mapQuestion, toApiIssueCode } from "./rates.map";
import type { ClarifyQuestion, RateIssueCode, RateRecord } from "./rates.model";
import { countByStatus, EQUIPMENT, PARSE_STEPS } from "./rates.model";

type ChatKind = "text" | "file" | "progress" | "question" | "date" | "guided" | "confirm" | "final";

type GuidedField = "validity" | "carrier" | "ports" | "amount" | "transit" | "equipment";

type ChatMessage = {
  id: string;
  who: "ai" | "me";
  kind: ChatKind;
  text?: string;
  fileName?: string;
  fileSize?: string;
  question?: ClarifyQuestion;
  confirmRef?: string;
  clarifyMode?: boolean;
  rateIds?: number[];
  pendingValidity?: boolean;
  guidedField?: GuidedField;
  pendingGuided?: boolean;
  done?: boolean;
  stale?: boolean;
  /** Human value shown on locked guided/date/confirm cards ("MSC", date range, …). */
  decidedSummary?: string;
};

const INTERACTIVE_KINDS = new Set<ChatKind>(["question", "date", "guided", "confirm"]);

const fileExtLabel = (fileName?: string): string => {
  const ext = fileName?.includes(".") ? fileName.split(".").pop()!.toLowerCase() : "";
  if (!ext) return "FILE";
  return ext.length <= 4 ? ext.toUpperCase() : ext.slice(0, 4).toUpperCase();
};

const fileExtTone = (fileName?: string): string => {
  const ext = fileName?.includes(".") ? fileName.split(".").pop()!.toLowerCase() : "";
  if (ext === "xlsx" || ext === "xls" || ext === "xlsm" || ext === "ods") return "sheet";
  if (ext === "csv") return "csv";
  if (ext === "pdf") return "pdf";
  if (ext === "doc" || ext === "docx") return "doc";
  if (ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "webp" || ext === "gif") return "img";
  if (ext === "txt" || ext === "md") return "text";
  return "file";
};

const guidedFieldLabel = (field?: GuidedField | string | null): string => {
  switch (field) {
    case "carrier":
      return "Carrier";
    case "ports":
      return "Origin / Destination";
    case "amount":
      return "Amount";
    case "transit":
      return "Transit days";
    case "equipment":
      return "Equipment";
    case "validity":
      return "Validity";
    default:
      return "Guided input";
  }
};

/** Open guided/date header: what is being clarified right now. */
const clarifyingTitle = (field?: GuidedField | string | null): string => `Clarifying · ${guidedFieldLabel(field)}`;

/** Canonical carrier placeholder (to be nominated). */
const CARRIER_TBN = CARRIER_TBN_NAME;

/** API/chat payload for one-field skip — BE isGuidedDeferMessage matches this (and bare "Skip"). */
const SKIP_ONE_MESSAGE = "Skip";

/** API/chat payload for bail-out — BE isClarifyModeManualMessage matches "Skip all" / "Skip all remaining". */
const SKIP_ALL_MESSAGE = "Skip all";

/** Locked status line: Field · value · done|skipped */
const lockedStatusLine = (fieldTitle: string, decided?: string, status?: string): string => {
  const state = status === "skipped" ? "skipped" : status === "done" || !status ? "done" : status;
  if (!decided || !decided.trim()) return `${fieldTitle} · ${state}`;
  let value = decided.trim();
  // Drop redundant "Carrier: MSC" / "Equipment: 40HQ" prefixes when title already names the field.
  const prefix = new RegExp(
    `^(?:${fieldTitle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}|Carrier|Equipment)\s*[:·-]\s*`,
    "i"
  );
  value = value.replace(prefix, "").trim() || value;
  return `${fieldTitle} · ${value} · ${state}`;
};

const interactiveSummary = (message: ChatMessage): string => {
  if (message.kind === "confirm") return message.clarifyMode ? "Update preference" : "Tracking confirm";
  if (message.guidedField) return guidedFieldLabel(message.guidedField);
  if (message.pendingValidity || message.kind === "date") return "Validity";
  if (message.question?.guidedFollowUp?.field) return guidedFieldLabel(message.question.guidedFollowUp.field);
  if (message.question?.key === "validity") return "Validity";
  if (message.question?.key === "charges") return "Charges";
  if (message.question?.key === "equip") return "Equipment";
  if (message.question?.key === "routing") return "Routing";
  if (message.question?.key === "dup") return "Conflict";
  if (message.question?.key === "expired") return "Expired";
  return "Question";
};

const lockPriorInteractives = (messages: ChatMessage[], exceptId?: string): ChatMessage[] =>
  messages.map((message) => {
    if (exceptId && message.id === exceptId) return message;
    if (!INTERACTIVE_KINDS.has(message.kind) || message.done) return message;
    return { ...message, done: true, stale: true };
  });

const isGuidedOpenOption = (option: string): boolean =>
  /set (?:validity|carrier|origin|destination|ports|amount|transit|equipment)|enter (?:equipment|amount|carrier|dates?)/i.test(
    option
  );

const isPortsOption = (option: string): boolean =>
  /set origin|set destination|origin\s*\/\s*destination|set ports/i.test(option);

const isAmountOption = (option: string): boolean => /set amount|enter amount/i.test(option);

const isEquipmentEnterOption = (option: string): boolean =>
  /enter (?:other )?equipment|other equipment|free[- ]?text/i.test(option);

const isCarrierOption = (option: string): boolean => /set carrier/i.test(option);

const hasSuggestedValidityDate = (ask: string): boolean =>
  /\b\d{1,2}\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{4}\b/i.test(ask) ||
  /\b\d{4}-\d{2}-\d{2}\b/.test(ask);

const isManualLaterOption = (option: string): boolean =>
  /manually update them later|edit these in the library|review individually|update them later/i.test(option);

const isValidityDatesOption = (option: string): boolean => /set validity|enter |another|validity dates/i.test(option);

type RatesImportProps = {
  rates: RateRecord[];
  sessionId: number | null;
  onSession: (session: RateImportSession) => void;
  onGoLibrary: () => void;
  onParseMeta?: (meta: { parsing: boolean; parsed: boolean; fixed: RateIssueCode[] }) => void;
  /** Keep mounted while other steps show — preserves chat state. */
  hidden?: boolean;
};

/** Drop a trailing half-tag (`<b`, `</b`, `<br`) and auto-close open `<b>` so typewriter never flashes raw markup. */
const forRichDisplay = (html: string): string => {
  let s = html.replace(/<[^>]*$/, "");
  const opens = (s.match(/<b>/g) ?? []).length;
  const closes = (s.match(/<\/b>/g) ?? []).length;
  if (opens > closes) s += "</b>".repeat(opens - closes);
  return s;
};

const rich = (html: string) => {
  const safe = forRichDisplay(html);
  const nodes: ReactNode[] = [];
  const pattern = /<b>(.*?)<\/b>|<br\s*\/?>/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(safe)) !== null) {
    if (match.index > cursor) nodes.push(safe.slice(cursor, match.index));
    if (match[0].startsWith("<br")) nodes.push(<br key={key} />);
    else nodes.push(<b key={key}>{match[1]}</b>);
    key += 1;
    cursor = match.index + match[0].length;
  }

  if (cursor < safe.length) nodes.push(safe.slice(cursor));

  return nodes;
};

const fileSize = (bytes: number): string =>
  bytes > 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const nextId = (() => {
  let n = 0;

  return () => `m-${++n}`;
})();

type TypewriterController = {
  enqueue: (streamId: string, delta: string) => void;
  /** Lock to one stream id; clear queue and ignore deltas for any other id. */
  arm: (streamId: string) => void;
  whenIdle: () => Promise<void>;
  flush: () => void;
  reset: () => void;
};

/** Reveal SSE deltas character-by-character; finishSession waits on whenIdle(). */
const createTypewriter = (
  append: (streamId: string, chunk: string) => void,
  onTypingId?: (id: string | null) => void,
  charsPerTick = 2,
  intervalMs = 22
): TypewriterController => {
  let queue = "";
  let activeId: string | null = null;
  let armedId: string | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;
  let waiters: Array<() => void> = [];

  const settle = () => {
    const done = waiters;
    waiters = [];
    done.forEach((resolve) => resolve());
  };

  const stopTimer = () => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };

  const flushQueueToActive = () => {
    if (activeId && queue.length > 0) {
      append(activeId, queue);
      onTypingId?.(activeId);
      queue = "";
    }
  };

  const tick = () => {
    if (!activeId || queue.length === 0) {
      stopTimer();
      settle();
      return;
    }
    const slice = queue.slice(0, charsPerTick);
    queue = queue.slice(charsPerTick);
    append(activeId, slice);
    onTypingId?.(activeId);
    if (queue.length === 0) {
      stopTimer();
      settle();
    }
  };

  return {
    arm(streamId) {
      queue = "";
      stopTimer();
      settle();
      armedId = streamId;
      activeId = streamId;
      onTypingId?.(streamId);
    },
    enqueue(streamId, delta) {
      if (!delta) return;
      // Ignore late/foreign deltas unless this id is armed.
      if (armedId === null || streamId !== armedId) return;
      if (streamId !== activeId) {
        // Flush leftover chars to the previous bubble before switching.
        flushQueueToActive();
        activeId = streamId;
      }
      queue += delta;
      onTypingId?.(streamId);
      if (timer === null) timer = setInterval(tick, intervalMs);
    },
    whenIdle() {
      if (timer === null && queue.length === 0) return Promise.resolve();
      return new Promise<void>((resolve) => waiters.push(resolve));
    },
    flush() {
      flushQueueToActive();
      stopTimer();
      settle();
      onTypingId?.(null);
    },
    reset() {
      queue = "";
      activeId = null;
      armedId = null;
      stopTimer();
      settle();
      onTypingId?.(null);
    },
  };
};

const PROGRESS_ID = "progress";

export const RatesImport = ({ rates, sessionId, onSession, onGoLibrary, onParseMeta, hidden }: RatesImportProps) => {
  const fileId = useId();
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messageCount = useRef(0);
  const [dropOver, setDropOver] = useState(false);
  const [draft, setDraft] = useState("");
  const [parsed, setParsed] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stepIndex, setStepIndex] = useState(-1);
  const stepIndexRef = useRef(-1);
  stepIndexRef.current = stepIndex;
  const progressTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const progressGenRef = useRef(0);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "hello",
      who: "ai",
      kind: "text",
      text: "Attach a tariff or just paste the rates as text — a lane per line works fine. I will read them, group whatever is unclear and ask you before anything is saved.",
    },
  ]);
  const [fixed, setFixed] = useState<Set<RateIssueCode>>(new Set());
  const [sessionCounts, setSessionCounts] = useState<RateImportSession["counts"] | null>(null);

  useEffect(() => {
    onParseMeta?.({ parsing, parsed, fixed: Array.from(fixed) });
  }, [parsing, parsed, fixed, onParseMeta]);
  const shownQuestionId = useRef<number | null>(null);
  const [typingId, setTypingId] = useState<string | null>(null);
  const typewriterRef = useRef<TypewriterController | null>(null);

  if (typewriterRef.current === null) {
    typewriterRef.current = createTypewriter(
      (streamId, chunk) => {
        setTypingId(streamId);
        setMessages((current) =>
          current.map((message) =>
            message.id === streamId ? { ...message, text: `${message.text ?? ""}${chunk}` } : message
          )
        );
      },
      (id) => setTypingId(id)
    );
  }

  useEffect(
    () => () => {
      typewriterRef.current?.reset();
      progressTimersRef.current.forEach((timer) => clearTimeout(timer));
      progressTimersRef.current = [];
      progressGenRef.current += 1;
    },
    []
  );

  const counts = countByStatus(rates);
  const expiredFromRates = rates.filter((rate) => rate.issueCodes.includes("expired")).length;
  const finalClean = sessionCounts?.clean ?? counts.ready;
  const finalExpired = sessionCounts?.expired ?? expiredFromRates;
  const finalNeedsInfo = sessionCounts?.needs_info ?? Math.max(0, counts.review - finalExpired);
  const locked = busy || parsing;

  useLayoutEffect(() => {
    const node = chatRef.current;
    if (!node) return;
    const viewport = window.visualViewport;

    const fit = () => {
      const vh = viewport?.height ?? window.innerHeight;
      const top = node.getBoundingClientRect().top;
      const room = Math.floor(vh - top - 12);
      const height = Math.max(280, Math.min(window.innerHeight, room));
      node.style.setProperty("--rt-chat-h", `${height}px`);
    };

    fit();
    window.addEventListener("resize", fit);
    viewport?.addEventListener("resize", fit);
    viewport?.addEventListener("scroll", fit);

    return () => {
      window.removeEventListener("resize", fit);
      viewport?.removeEventListener("resize", fit);
      viewport?.removeEventListener("scroll", fit);
    };
  }, []);

  const nearBottomRef = useRef(true);
  const pinnedInteractiveRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const onScroll = () => {
      nearBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight <= 80;
    };

    onScroll();
    node.addEventListener("scroll", onScroll, { passive: true });
    return () => node.removeEventListener("scroll", onScroll);
  }, []);

  const busyRef = useRef(busy);
  const parsingRef = useRef(parsing);
  busyRef.current = busy;
  parsingRef.current = parsing;

  // Stable follower: keep bottom pinned during typewriter growth without rebinding every character.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const thread = node.querySelector(".rt-th");

    const stickBottom = () => {
      if (!nearBottomRef.current) return;
      // Instant jump — smooth scroll + line wraps fights the typewriter and looks flickery.
      node.scrollTop = node.scrollHeight;
    };

    const ro = new ResizeObserver(stickBottom);
    ro.observe(node);
    if (thread instanceof HTMLElement) ro.observe(thread);

    const mo = new MutationObserver(() => {
      const last = thread?.lastElementChild ?? node.lastElementChild;
      if (last instanceof HTMLElement) ro.observe(last);
      stickBottom();
    });
    mo.observe(thread ?? node, { childList: true, subtree: true, characterData: true });

    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const added = messages.length !== messageCount.current;
    messageCount.current = messages.length;

    const scrollBottom = (smooth: boolean) => {
      if (smooth && !busyRef.current && !parsingRef.current) {
        node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
      } else {
        node.scrollTop = node.scrollHeight;
      }
    };

    // Pin each new interactive once (not on every typewriter tick).
    const newestInteractive = [...messages]
      .reverse()
      .find((message) => INTERACTIVE_KINDS.has(message.kind) && !message.done);
    if (newestInteractive && !pinnedInteractiveRef.current.has(newestInteractive.id)) {
      pinnedInteractiveRef.current.add(newestInteractive.id);
      const target = node.querySelector(`[data-rt-msg="${newestInteractive.id}"]`);
      if (target instanceof HTMLElement) {
        target.scrollIntoView({ block: "nearest", behavior: busyRef.current ? "auto" : "smooth" });
      } else if (nearBottomRef.current) {
        scrollBottom(true);
      }
    } else if (added && nearBottomRef.current) {
      scrollBottom(true);
    }
  }, [messages.length, messages.map((m) => `${m.id}:${m.kind}:${m.done ? 1 : 0}`).join("|")]);

  const push = (message: Omit<ChatMessage, "id"> & { id?: string }) => {
    const id = message.id ?? nextId();
    setMessages((current) => [...current, { ...message, id }]);

    return id;
  };

  /** Flush prior typewriter, disarm, push empty AI bubble, arm for new stream. */
  const beginAiStream = () => {
    typewriterRef.current?.flush();
    typewriterRef.current?.reset();
    const streamId = push({ who: "ai", kind: "text", text: "" });
    typewriterRef.current?.arm(streamId);
    return streamId;
  };

  const finishSession = (session: RateImportSession, streamId?: string) => {
    const found = (session.counts?.total ?? session.rates.length) > 0;
    if (session.counts) {
      setSessionCounts(session.counts);
    }
    if (found) {
      onSession(session);
      setParsed(true);
      setStepIndex(PARSE_STEPS.length);
    }
    setParsing(false);
    setBusy(false);
    keepComposerFocus();

    const summary = `${session.counts.total} rates detected · ${session.counts.clean} ready · ${session.counts.needs_info} require clarification`;
    const resolvedIds = new Set(session.issues.filter((issue) => issue.resolved).map((issue) => issue.id));
    const openId = session.current_question && !session.current_question.resolved ? session.current_question.id : null;
    const pending = session.pending_action;
    setMessages((current) => {
      const next = current
        .filter((message) => found || message.id !== PROGRESS_ID)
        .map((message) => {
          if (found && message.id === PROGRESS_ID) return { ...message, done: true, text: summary };
          if (streamId && message.id === streamId && session.display_message) {
            // Prefer live typed deltas; only replace when the bubble is empty or diverged.
            if (!message.text || message.text === session.display_message) {
              return message.text === session.display_message ? message : { ...message, text: session.display_message };
            }
            return { ...message, text: session.display_message };
          }
          if (message.kind === "question" && message.question?.id != null && resolvedIds.has(message.question.id)) {
            return { ...message, done: true, stale: false };
          }
          // Newer turn superseded this interactive (new question / pending card incoming).
          if (INTERACTIVE_KINDS.has(message.kind) && !message.done) {
            if (message.kind === "question" && message.question?.id != null && openId === message.question.id) {
              return message;
            }
            if (message.kind === "date" && message.pendingValidity && pending?.type === "validity") {
              return message;
            }
            if (
              message.kind === "guided" &&
              message.pendingGuided &&
              pending?.type === "guided" &&
              pending.field === message.guidedField
            ) {
              return message;
            }
            if (message.kind === "confirm" && pending?.type === "track" && message.confirmRef === pending.reference) {
              return message;
            }
            if (message.kind === "confirm" && message.clarifyMode && pending?.type === "clarify_mode") {
              return message;
            }
            // Keep the currently shown open question until a newer interactive is pushed below.
            if (
              message.kind === "question" &&
              openId != null &&
              message.question?.id === openId &&
              pending?.type !== "validity" &&
              pending?.type !== "guided" &&
              pending?.type !== "track" &&
              pending?.type !== "clarify_mode"
            ) {
              return message;
            }
            return { ...message, done: true, stale: true };
          }
          return message;
        });

      if (streamId && session.display_message && !next.some((message) => message.id === streamId)) {
        next.push({ id: streamId, who: "ai", kind: "text", text: session.display_message });
      }

      return next;
    });

    if (!streamId && session.display_message) {
      push({ who: "ai", kind: "text", text: session.display_message });
    }

    if (!found) return;

    const open = session.current_question;
    const manualClarify = session.clarify_mode === "manual";
    if (session.pending_action?.type === "clarify_mode") {
      shownQuestionId.current = null;
      setMessages((current) => {
        if (current.some((message) => message.kind === "confirm" && message.clarifyMode && !message.done)) {
          return current;
        }
        // Prefer attaching buttons onto the streamed ask bubble when present.
        if (streamId && current.some((message) => message.id === streamId)) {
          return current.map((message) =>
            message.id === streamId
              ? {
                  ...message,
                  kind: "confirm",
                  clarifyMode: true,
                  text: message.text || session.display_message || undefined,
                }
              : message
          );
        }
        return [
          ...lockPriorInteractives(current),
          {
            id: nextId(),
            who: "ai",
            kind: "confirm",
            clarifyMode: true,
            text:
              session.display_message ??
              "Looks like some of your rates are missing data. Update them in chat, or I'll leave them for you to fix manually in Data Review?",
          },
        ];
      });
    } else if (session.pending_action?.type === "validity") {
      const rateIds = session.pending_action.rate_ids ?? [];
      setMessages((current) => {
        if (current.some((message) => message.kind === "date" && message.pendingValidity && !message.done)) {
          return current;
        }
        return [
          ...lockPriorInteractives(current),
          {
            id: nextId(),
            who: "ai",
            kind: "date",
            text: "From / Until",
            rateIds,
            pendingValidity: true,
            guidedField: "validity",
          },
        ];
      });
    } else if (session.pending_action?.type === "guided") {
      const rateIds = session.pending_action.rate_ids ?? [];
      const field = session.pending_action.field;
      setMessages((current) => {
        if (
          current.some(
            (message) =>
              message.kind === "guided" && message.pendingGuided && message.guidedField === field && !message.done
          )
        ) {
          return current;
        }
        return [
          ...lockPriorInteractives(current),
          {
            id: nextId(),
            who: "ai",
            kind: "guided",
            text: guidedFieldLabel(field),
            rateIds,
            guidedField: field,
            pendingGuided: true,
          },
        ];
      });
    } else if (open && !open.resolved && !manualClarify) {
      if (shownQuestionId.current !== open.id) {
        shownQuestionId.current = open.id;
        setMessages((current) => [
          ...lockPriorInteractives(current),
          { id: nextId(), who: "ai", kind: "question", question: mapQuestion(open) },
        ]);
      }
    } else if (session.pending_action?.type === "track" && session.pending_action.reference) {
      const ref = session.pending_action.reference;
      setMessages((current) => {
        if (streamId && current.some((message) => message.id === streamId)) {
          return current.map((message) =>
            message.id === streamId
              ? {
                  ...message,
                  kind: "confirm",
                  confirmRef: ref,
                  text: message.text || session.display_message || undefined,
                }
              : message
          );
        }
        if (current.some((message) => message.kind === "confirm" && message.confirmRef === ref && !message.done)) {
          return current;
        }
        return [
          ...lockPriorInteractives(current),
          {
            id: nextId(),
            who: "ai",
            kind: "confirm",
            confirmRef: ref,
            text:
              session.display_message ??
              `Container Tracker can look up <b>${ref}</b>. This uses <b>1 search</b> on your platform plan.`,
          },
        ];
      });
    } else if (
      ((session.counts.issues_found ?? 0) === 0 && (session.counts.total ?? 0) > 0) ||
      (manualClarify && (session.counts.needs_info ?? 0) > 0)
    ) {
      setMessages((current) =>
        current.some((message) => message.kind === "final")
          ? current
          : [...current, { id: nextId(), who: "ai", kind: "final" }]
      );
    }

    setFixed((current) => {
      const next = new Set(current);
      session.issues
        .filter((issue) => issue.resolved)
        .forEach((issue) => {
          next.add(
            issue.code === "equipment" ? "equip" : issue.code === "conflict" ? "dup" : (issue.code as RateIssueCode)
          );
        });

      return next;
    });
  };

  const clearFakeProgress = () => {
    progressTimersRef.current.forEach((timer) => clearTimeout(timer));
    progressTimersRef.current = [];
  };

  const ensureProgressCard = (streamId: string) => {
    setMessages((current) => {
      const withoutOldProgress = current.filter((message) => message.id !== PROGRESS_ID);
      const progress: ChatMessage = { id: PROGRESS_ID, who: "ai", kind: "progress" };
      const index = withoutOldProgress.findIndex((message) => message.id === streamId);
      if (index === -1) return [...withoutOldProgress, progress];
      return [...withoutOldProgress.slice(0, index), progress, ...withoutOldProgress.slice(index)];
    });
  };

  /** FE-only theatre: backend dumps all statuses at once before the real model call. */
  const startFakeProgress = (streamId: string) => {
    clearFakeProgress();
    const gen = ++progressGenRef.current;
    setParsing(true);
    setStepIndex(0);
    stepIndexRef.current = 0;
    ensureProgressCard(streamId);

    let delay = 0;
    for (let i = 1; i < PARSE_STEPS.length; i += 1) {
      // ~0.5–1.1s between steps so it feels like work, not a spinner freeze on "charges".
      delay += 500 + Math.floor(Math.random() * 600);
      const step = i;
      const timer = setTimeout(() => {
        if (progressGenRef.current !== gen) return;
        setStepIndex(step);
        stepIndexRef.current = step;
      }, delay);
      progressTimersRef.current.push(timer);
    }
  };

  const finishFakeProgress = (): Promise<void> =>
    new Promise((resolve) => {
      clearFakeProgress();
      const gen = ++progressGenRef.current;
      const last = PARSE_STEPS.length - 1;
      let current = stepIndexRef.current;

      if (current < 0) {
        current = 0;
        setStepIndex(0);
        stepIndexRef.current = 0;
      }

      const advance = () => {
        if (progressGenRef.current !== gen) {
          resolve();
          return;
        }
        if (current >= last) {
          setStepIndex(PARSE_STEPS.length);
          stepIndexRef.current = PARSE_STEPS.length;
          resolve();
          return;
        }
        current += 1;
        setStepIndex(current);
        stepIndexRef.current = current;
        const timer = setTimeout(advance, 70 + Math.floor(Math.random() * 50));
        progressTimersRef.current.push(timer);
      };

      advance();
    });

  const fail = (message: string) => {
    clearFakeProgress();
    progressGenRef.current += 1;
    setParsing(false);
    setBusy(false);
    keepComposerFocus();
    push({ who: "ai", kind: "text", text: message });
  };

  const bindParseStream = (streamId: string, start: (callbacks: Parameters<typeof streamRatesImport>[2]) => void) => {
    setBusy(true);
    startFakeProgress(streamId);
    start({
      onStatus: () => {
        // Ignore backend status pacing — checklist is FE-faked. Keep the card mounted.
        ensureProgressCard(streamId);
      },
      onDelta: (delta) => typewriterRef.current?.enqueue(streamId, delta),
      onComplete: (session) => {
        void (async () => {
          await finishFakeProgress();
          await typewriterRef.current?.whenIdle();
          typewriterRef.current?.reset();
          finishSession(session, streamId);
        })();
      },
      onError: (message) => {
        typewriterRef.current?.flush();
        typewriterRef.current?.reset();
        fail(message);
      },
    });
  };

  const beginInitiate = (text: string, files: File[]) => {
    if (parsed || parsing || busy) return;
    const streamId = beginAiStream();
    bindParseStream(streamId, (callbacks) => {
      void streamRatesImport(text, files, callbacks);
    });
  };

  /** After the first parse, more files merge into the open session (backend resets clarify_mode). */
  const beginMergeFiles = (files: File[]) => {
    if (sessionId == null || parsing || busy) return;
    const streamId = beginAiStream();
    bindParseStream(streamId, (callbacks) => {
      void streamRatesReply(sessionId, { message: "", files }, callbacks);
    });
  };

  const attachFiles = (list: FileList | File[]) => {
    const files = Array.from(list);
    if (files.length === 0 || parsing || busy) return;

    setMessages((current) => {
      let next = lockPriorInteractives(current).filter((message) => message.id !== PROGRESS_ID);
      files.forEach((file) => {
        next = [
          ...next,
          {
            id: nextId(),
            who: "me",
            kind: "file",
            fileName: file.name,
            fileSize: fileSize(file.size),
          },
        ];
      });
      return next;
    });

    if (!parsed) {
      beginInitiate("", files);
      return;
    }

    if (sessionId != null) {
      beginMergeFiles(files);
      return;
    }

    // Parsed UI without a session id — start a fresh import (don't rely on setParsed async).
    setParsed(false);
    const streamId = beginAiStream();
    bindParseStream(streamId, (callbacks) => {
      void streamRatesImport("", files, callbacks);
    });
  };

  const keepComposerFocus = () => {
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  const sendText = () => {
    const value = draft.trim();
    if (!value || locked) return;
    setMessages((current) => [
      ...lockPriorInteractives(current),
      { id: nextId(), who: "me", kind: "text", text: value },
    ]);
    setDraft("");
    keepComposerFocus();

    if (!parsed && !parsing) {
      beginInitiate(value, []);

      return;
    }

    if (sessionId == null) return;

    setBusy(true);
    const streamId = beginAiStream();

    void streamRatesReply(
      sessionId,
      { message: value },
      {
        onDelta: (delta) => typewriterRef.current?.enqueue(streamId, delta),
        onComplete: (session) => {
          void typewriterRef.current?.whenIdle().then(() => {
            typewriterRef.current?.reset();
            finishSession(session, streamId);
          });
        },
        onError: (message) => {
          typewriterRef.current?.flush();
          typewriterRef.current?.reset();
          fail(message);
        },
      }
    );
  };

  const markDone = (id: string, decidedSummary?: string) =>
    setMessages((current) =>
      current.map((message) =>
        message.id === id
          ? {
              ...message,
              done: true,
              decidedSummary: decidedSummary ?? message.decidedSummary,
            }
          : message
      )
    );

  const resolve = (
    messageId: string,
    question: ClarifyQuestion,
    individual: boolean,
    extra?: string,
    customIso?: string
  ) => {
    if (sessionId == null || locked) return;

    const suggested = hasSuggestedValidityDate(question.ask);
    // Validity without a concrete suggested window must go through the date pickers (custom).
    if (question.key === "validity" && !individual && !customIso && !suggested) {
      return;
    }

    const decided =
      extra ?? (individual ? "I'll manually update them later" : customIso ? customIso : "Yes, apply to all");
    markDone(messageId, decided);
    setBusy(true);
    setFixed((current) => new Set(current).add(question.key));
    keepComposerFocus();

    const action = individual ? "review_individually" : customIso ? "custom" : "apply_all";
    const streamId = beginAiStream();

    void streamRatesReply(
      sessionId,
      {
        message: extra ?? (individual ? "I'll manually update them later" : "Yes, apply to all"),
        issue_code: toApiIssueCode(question.key),
        action,
        custom_value: customIso,
      },
      {
        onDelta: (delta) => typewriterRef.current?.enqueue(streamId, delta),
        onComplete: (session) => {
          void typewriterRef.current?.whenIdle().then(() => {
            typewriterRef.current?.reset();
            finishSession(session, streamId);
          });
        },
        onError: (message) => {
          typewriterRef.current?.flush();
          typewriterRef.current?.reset();
          fail(message);
        },
      }
    );
  };

  const confirmTrack = (messageId: string, reference: string, proceed: boolean) => {
    if (sessionId == null || locked) return;
    const label = proceed ? "Yes, track it" : "Cancel";
    markDone(messageId, label);
    push({ who: "me", kind: "text", text: label });
    setBusy(true);
    keepComposerFocus();
    const streamId = beginAiStream();

    void streamRatesReply(
      sessionId,
      { message: proceed ? `Yes, track it ${reference}` : "Cancel tracking" },
      {
        onDelta: (delta) => typewriterRef.current?.enqueue(streamId, delta),
        onComplete: (session) => {
          void typewriterRef.current?.whenIdle().then(() => {
            typewriterRef.current?.reset();
            finishSession(session, streamId);
          });
        },
        onError: (message) => {
          typewriterRef.current?.flush();
          typewriterRef.current?.reset();
          fail(message);
        },
      }
    );
  };

  const confirmClarifyMode = (messageId: string, mode: "chat" | "manual") => {
    if (sessionId == null || locked) return;
    const label = mode === "chat" ? "Update via chat" : "I'll update manually";
    markDone(messageId, label);
    push({ who: "me", kind: "text", text: label });
    setBusy(true);
    keepComposerFocus();
    const streamId = beginAiStream();

    void streamRatesReply(
      sessionId,
      { message: label },
      {
        onDelta: (delta) => typewriterRef.current?.enqueue(streamId, delta),
        onComplete: (session) => {
          void typewriterRef.current?.whenIdle().then(() => {
            typewriterRef.current?.reset();
            finishSession(session, streamId);
          });
        },
        onError: (message) => {
          typewriterRef.current?.flush();
          typewriterRef.current?.reset();
          fail(message);
        },
      }
    );
  };

  const applyPendingValidity = (messageId: string, label: string, isoRange: string) => {
    if (sessionId == null || locked) return;
    markDone(messageId, label);
    push({ who: "me", kind: "text", text: label });
    setBusy(true);
    keepComposerFocus();
    const streamId = beginAiStream();

    // Free-text ISO range so backend pending_action handler applies to stashed rate_ids.
    void streamRatesReply(
      sessionId,
      { message: isoRange },
      {
        onDelta: (delta) => typewriterRef.current?.enqueue(streamId, delta),
        onComplete: (session) => {
          void typewriterRef.current?.whenIdle().then(() => {
            typewriterRef.current?.reset();
            finishSession(session, streamId);
          });
        },
        onError: (message) => {
          typewriterRef.current?.flush();
          typewriterRef.current?.reset();
          fail(message);
        },
      }
    );
  };

  const applyPendingGuided = (messageId: string, label: string, payload: string) => {
    if (sessionId == null || locked) return;
    markDone(messageId, label);
    push({ who: "me", kind: "text", text: label });
    setBusy(true);
    keepComposerFocus();
    const streamId = beginAiStream();

    void streamRatesReply(
      sessionId,
      { message: payload },
      {
        onDelta: (delta) => typewriterRef.current?.enqueue(streamId, delta),
        onComplete: (session) => {
          void typewriterRef.current?.whenIdle().then(() => {
            typewriterRef.current?.reset();
            finishSession(session, streamId);
          });
        },
        onError: (message) => {
          typewriterRef.current?.flush();
          typewriterRef.current?.reset();
          fail(message);
        },
      }
    );
  };

  const skipPendingGuided = (messageId: string) => {
    applyPendingGuided(messageId, "Skip", SKIP_ONE_MESSAGE);
  };

  /** Bail out of ALL remaining guided/date/question clarifications for this batch. */
  const skipAllClarifications = (messageId: string) => {
    applyPendingGuided(messageId, "Skip all", SKIP_ALL_MESSAGE);
  };

  const guidedRateHint = (rateIds?: number[], field?: GuidedField | string | null): string => {
    const fieldBit = field ? `Step: missing ${guidedFieldLabel(field).toLowerCase()}` : null;
    const withStep = (lane: string) => (fieldBit ? `${fieldBit} · ${lane}` : lane);
    if (!rateIds || rateIds.length === 0) {
      return withStep("Matching rates in this session");
    }
    const idSet = new Set(rateIds.map(String));
    const matched = rates.filter((rate) => idSet.has(String(rate.id)));
    const labelFor = (rate: RateRecord): string => {
      const origin = (rate.origin ?? "").trim();
      const destination = (rate.destination ?? "").trim();
      const equipment = (rate.equipment ?? "").trim();
      if (origin && destination && origin !== "—" && destination !== "—") {
        const eq = equipment && equipment !== "—" ? ` ${equipment}` : "";
        return `${origin}→${destination}${eq}`;
      }
      return `#${rate.id}`;
    };
    if (matched.length === 1) return withStep(`Lane ${labelFor(matched[0])}`);
    if (matched.length > 1) {
      const examples = matched.slice(0, 3).map(labelFor);
      const extra = matched.length - examples.length;
      return withStep(`Lanes ${examples.join(", ")}${extra > 0 ? ` and ${extra} more` : ""}`);
    }
    if (rateIds.length <= 6) return withStep(`Rates ${rateIds.map((id) => `#${id}`).join(", ")}`);
    return withStep(`${rateIds.length} rates in this session`);
  };

  return (
    <div className="rt-pane" hidden={hidden}>
      <div className="rt-ai">
        <div className="pcx rt-ai__chat" ref={chatRef}>
          <div className="rt-ai__scroll" ref={scrollRef}>
            <div className="pcx__top">
              <div>
                <div className="pcx__h">Add Logistics Data</div>
                <p className="pcx__sub">
                  Drop a tariff in any format — Excel, PDF, image, email. QSearch reads the commercial meaning of every
                  rate, flags what is unclear and asks you before saving.
                </p>
              </div>
            </div>

            <div
              className={cn("rt-drop", { "is-over": dropOver })}
              onDragEnter={(event) => {
                event.preventDefault();
                setDropOver(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => {
                const next = event.relatedTarget;
                if (next instanceof Node && event.currentTarget.contains(next)) return;
                setDropOver(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setDropOver(false);
                if (event.dataTransfer.files.length) attachFiles(event.dataTransfer.files);
              }}
            >
              <input
                ref={fileRef}
                id={fileId}
                type="file"
                hidden
                multiple
                onChange={(event) => {
                  if (event.target.files?.length) attachFiles(event.target.files);
                  event.target.value = "";
                }}
              />
              <p>
                <b>Drop the tariff here</b>
                <em>XLSX, CSV, PDF, DOCX, PNG, EML — any format, up to 25 MB</em>
              </p>
              <button type="button" className="pcbtn pcbtn--main" onClick={() => fileRef.current?.click()}>
                Choose file
              </button>
            </div>

            <div className="rt-chat">
              <div className="rt-th">
                {messages.map((message) => {
                  const guidedShell = message.kind === "guided" || message.kind === "date" || message.kind === "final";
                  const bubble = (
                    <>
                      {message.kind === "progress" && (
                        <div className="rt-prog">
                          <div className="rt-prog__hd">
                            <b>
                              {message.done
                                ? "Parsing complete"
                                : stepIndex >= 0 && stepIndex < PARSE_STEPS.length
                                  ? `${PARSE_STEPS[stepIndex].label}…`
                                  : "Parsing…"}
                            </b>
                            <span>{message.done && message.text ? message.text : "Working through your file"}</span>
                          </div>
                          <div className="rt-prog__bar">
                            <i
                              style={{
                                width: `${Math.round(((message.done ? PARSE_STEPS.length : Math.max(stepIndex + 1, 0)) / PARSE_STEPS.length) * 100)}%`,
                              }}
                            />
                          </div>
                          <ul className="rt-steps">
                            {PARSE_STEPS.map((step, index) => (
                              <li
                                key={step.key}
                                className={cn({
                                  "is-now": !message.done && index === stepIndex,
                                  "is-done": message.done || index < stepIndex,
                                })}
                              >
                                {step.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {message.kind === "file" && (
                        <div className="rt-m__file">
                          <span
                            className={cn("rt-m__file-ico", `rt-m__file-ico--${fileExtTone(message.fileName)}`)}
                            aria-hidden="true"
                          >
                            <em>{fileExtLabel(message.fileName)}</em>
                          </span>
                          <div>
                            <b>{message.fileName}</b>
                            <em>{message.fileSize}</em>
                          </div>
                        </div>
                      )}
                      {message.kind === "text" && (
                        <div className="rt-m__stream">
                          {message.text ? (
                            <div>{rich(message.text)}</div>
                          ) : (
                            <div className="rt-think" aria-live="polite" aria-label="Thinking">
                              <span className="rt-think__label">Thinking</span>
                              <span className="rt-think__dots" aria-hidden="true">
                                <i />
                                <i />
                                <i />
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {message.kind === "question" && message.question && (
                        <>
                          <div>{rich(message.question.ask)}</div>
                          {message.done ? (
                            <GuidedBlock
                              title={interactiveSummary(message)}
                              locked
                              status={message.stale ? "skipped" : "done"}
                              decided={message.decidedSummary}
                            />
                          ) : (
                            <>
                              <div className="rt-q">
                                {message.question.options
                                  .filter((option) => {
                                    // Pickers / guided forms already visible — hide redundant primary + later chip.
                                    if (
                                      message.question?.dateFollowUp &&
                                      message.question.key === "validity" &&
                                      isValidityDatesOption(option)
                                    ) {
                                      return false;
                                    }
                                    if (message.question?.guidedFollowUp?.field === "ports" && isPortsOption(option)) {
                                      return false;
                                    }
                                    if (
                                      message.question?.guidedFollowUp?.field === "amount" &&
                                      isAmountOption(option)
                                    ) {
                                      return false;
                                    }
                                    if (
                                      message.question?.guidedFollowUp?.field === "equipment" &&
                                      isEquipmentEnterOption(option)
                                    ) {
                                      return false;
                                    }
                                    // Footer of DateFollowUp / GuidedInput already has the skip control.
                                    if (
                                      isManualLaterOption(option) &&
                                      (message.question?.dateFollowUp || message.question?.guidedFollowUp)
                                    ) {
                                      return false;
                                    }
                                    return true;
                                  })
                                  .map((option, index) => (
                                    <button
                                      key={option}
                                      type="button"
                                      className={cn("pcbtn", {
                                        "pcbtn--main":
                                          index === 0 &&
                                          !(
                                            (message.question?.dateFollowUp && message.question.key === "validity") ||
                                            message.question?.guidedFollowUp
                                          ),
                                      })}
                                      disabled={locked}
                                      onMouseDown={(event) => event.preventDefault()}
                                      onClick={() => {
                                        const question = message.question as ClarifyQuestion;
                                        push({ who: "me", kind: "text", text: option });

                                        if (isManualLaterOption(option)) {
                                          resolve(message.id, question, true, option);
                                          return;
                                        }

                                        if (
                                          (isValidityDatesOption(option) ||
                                            (/apply to all/i.test(option) &&
                                              question.key === "validity" &&
                                              !hasSuggestedValidityDate(question.ask))) &&
                                          question.dateFollowUp
                                        ) {
                                          const root = document.getElementById(`rt-date-${message.id}`);
                                          root
                                            ?.querySelector<HTMLButtonElement>("button[data-validity-trigger]")
                                            ?.focus();
                                          return;
                                        }

                                        if (
                                          question.guidedFollowUp &&
                                          (isGuidedOpenOption(option) ||
                                            isPortsOption(option) ||
                                            isAmountOption(option) ||
                                            isEquipmentEnterOption(option) ||
                                            isCarrierOption(option))
                                        ) {
                                          const root = document.getElementById(`rt-guided-${message.id}`);
                                          root?.querySelector<HTMLElement>("input, select, .pcsel__btn")?.focus();
                                          return;
                                        }

                                        if (
                                          question.key === "validity" &&
                                          /apply to all/i.test(option) &&
                                          !hasSuggestedValidityDate(question.ask)
                                        ) {
                                          return;
                                        }

                                        const individual = isManualLaterOption(option);
                                        const extra =
                                          question.key === "equip" && /40ST|40HQ/.test(option)
                                            ? option === "40ST"
                                              ? "40'ST"
                                              : "40'HQ"
                                            : question.key === "charges" && /Included|Excluded/.test(option)
                                              ? option
                                              : question.key === "dup"
                                                ? option
                                                : question.key === "routing" && /Yes|No/.test(option)
                                                  ? option
                                                  : undefined;
                                        resolve(message.id, question, individual, extra);
                                      }}
                                    >
                                      {option}
                                    </button>
                                  ))}
                                {!(message.question.dateFollowUp || message.question.guidedFollowUp) ? (
                                  <button
                                    type="button"
                                    className="pcbtn"
                                    disabled={locked}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => skipAllClarifications(message.id)}
                                  >
                                    Skip all
                                  </button>
                                ) : null}
                              </div>
                              {message.question.dateFollowUp && message.question.key === "validity" && (
                                <div id={`rt-date-${message.id}`}>
                                  <DateFollowUp
                                    label={message.question.dateFollowUp.label}
                                    hint={guidedRateHint(message.question.rateIds, "validity")}
                                    onApply={(label, isoRange) => {
                                      push({ who: "me", kind: "text", text: label });
                                      resolve(message.id, message.question as ClarifyQuestion, false, label, isoRange);
                                    }}
                                    onSkip={() => {
                                      push({ who: "me", kind: "text", text: "Skip" });
                                      resolve(message.id, message.question as ClarifyQuestion, true, SKIP_ONE_MESSAGE);
                                    }}
                                    onSkipAll={() => skipAllClarifications(message.id)}
                                  />
                                </div>
                              )}
                              {message.question.guidedFollowUp && (
                                <div id={`rt-guided-${message.id}`}>
                                  <GuidedInput
                                    field={message.question.guidedFollowUp.field}
                                    label={message.question.guidedFollowUp.label}
                                    hint={guidedRateHint(
                                      message.question.rateIds,
                                      message.question.guidedFollowUp.field
                                    )}
                                    rateIds={message.question.rateIds}
                                    rates={rates}
                                    onApply={(label, payload) => {
                                      push({ who: "me", kind: "text", text: label });
                                      resolve(message.id, message.question as ClarifyQuestion, false, label, payload);
                                    }}
                                    onSkip={() => {
                                      push({ who: "me", kind: "text", text: "Skip" });
                                      resolve(message.id, message.question as ClarifyQuestion, true, SKIP_ONE_MESSAGE);
                                    }}
                                    onSkipAll={() => skipAllClarifications(message.id)}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                      {message.kind === "date" && (message.question || message.pendingValidity) && (
                        <DateFollowUp
                          label={message.text ?? ""}
                          hint={
                            message.pendingValidity
                              ? guidedRateHint(message.rateIds, "validity")
                              : guidedRateHint(message.question?.rateIds, "validity")
                          }
                          locked={Boolean(message.done)}
                          status={message.stale ? "skipped" : "done"}
                          decided={message.decidedSummary}
                          onApply={(label, isoRange) => {
                            if (message.pendingValidity) {
                              applyPendingValidity(message.id, label, isoRange);
                              return;
                            }
                            push({ who: "me", kind: "text", text: label });
                            resolve(message.id, message.question as ClarifyQuestion, false, label, isoRange);
                          }}
                          onSkip={
                            message.pendingValidity
                              ? () => {
                                  // Stay in chat: skip without forcing library navigation.
                                  applyPendingGuided(message.id, "Skip", SKIP_ONE_MESSAGE);
                                }
                              : message.question
                                ? () => {
                                    push({ who: "me", kind: "text", text: "Skip" });
                                    resolve(message.id, message.question as ClarifyQuestion, true, SKIP_ONE_MESSAGE);
                                  }
                                : undefined
                          }
                          onSkipAll={
                            message.pendingValidity || message.question
                              ? () => skipAllClarifications(message.id)
                              : undefined
                          }
                        />
                      )}
                      {message.kind === "guided" && message.guidedField && (
                        <GuidedInput
                          field={message.guidedField}
                          label={message.text ?? guidedFieldLabel(message.guidedField)}
                          hint={guidedRateHint(message.rateIds, message.guidedField)}
                          rateIds={message.rateIds}
                          rates={rates}
                          locked={Boolean(message.done)}
                          status={message.stale ? "skipped" : "done"}
                          decided={message.decidedSummary}
                          onApply={(label, payload) => {
                            if (message.pendingGuided) {
                              applyPendingGuided(message.id, label, payload);
                              return;
                            }
                            push({ who: "me", kind: "text", text: label });
                            if (message.question) {
                              resolve(message.id, message.question, false, label, payload);
                            }
                          }}
                          onSkip={
                            message.pendingGuided
                              ? () => skipPendingGuided(message.id)
                              : message.question
                                ? () => {
                                    push({ who: "me", kind: "text", text: "Skip" });
                                    resolve(message.id, message.question as ClarifyQuestion, true, SKIP_ONE_MESSAGE);
                                  }
                                : undefined
                          }
                          onSkipAll={
                            message.pendingGuided || message.question
                              ? () => skipAllClarifications(message.id)
                              : undefined
                          }
                        />
                      )}
                      {message.kind === "confirm" && message.clarifyMode && (
                        <>
                          <div>{rich(message.text ?? "")}</div>
                          {message.done ? (
                            <GuidedBlock
                              title="Update preference"
                              locked
                              status={message.stale ? "skipped" : "done"}
                              decided={message.decidedSummary}
                            />
                          ) : (
                            <GuidedBlock
                              className="rt-guided--compact"
                              title="Clarifying · How to update"
                              hint="Choose how you want to finish incomplete rates for this batch."
                            >
                              <div className="rt-q">
                                <button
                                  type="button"
                                  className="pcbtn pcbtn--main"
                                  disabled={locked}
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => confirmClarifyMode(message.id, "chat")}
                                >
                                  Update via chat
                                </button>
                                <button
                                  type="button"
                                  className="pcbtn"
                                  disabled={locked}
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => confirmClarifyMode(message.id, "manual")}
                                >
                                  I&apos;ll update manually
                                </button>
                              </div>
                            </GuidedBlock>
                          )}
                        </>
                      )}
                      {message.kind === "confirm" && message.confirmRef && !message.clarifyMode && (
                        <>
                          <div>{rich(message.text ?? "")}</div>
                          {message.done ? (
                            <GuidedBlock
                              title="Tracking confirm"
                              locked
                              status={message.stale ? "skipped" : "done"}
                              decided={message.decidedSummary}
                            />
                          ) : (
                            <div className="rt-q">
                              <button
                                type="button"
                                className="pcbtn pcbtn--main"
                                disabled={locked}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => confirmTrack(message.id, message.confirmRef as string, true)}
                              >
                                Yes, track it
                              </button>
                              <button
                                type="button"
                                className="pcbtn"
                                disabled={locked}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => confirmTrack(message.id, message.confirmRef as string, false)}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </>
                      )}
                      {message.kind === "final" && (
                        <div className="rt-final">
                          <div className="rt-final__hd">
                            <b>{rates.length} rates processed</b>
                            <span>
                              {finalNeedsInfo > 0
                                ? "Incomplete rates stay in Needs Information — open Data Review when you're ready. Expired rates stay in the library but will not be activated."
                                : "All grouped questions are resolved. Expired rates stay in the library but will not be activated."}
                            </span>
                          </div>
                          <div className="rt-final__n">
                            <span className="rt-st rt-st--ok">{finalClean} Clean Rates</span>
                            {finalNeedsInfo > 0 ? (
                              <span className="rt-st rt-st--warn">{finalNeedsInfo} Needs Information</span>
                            ) : null}
                            {finalExpired > 0 ? <span className="rt-st rt-st--exp">{finalExpired} Expired</span> : null}
                          </div>
                          <div className="rt-c__btns">
                            <button type="button" className="pcbtn pcbtn--main" onClick={onGoLibrary}>
                              Data Review &amp; Edit
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                  return (
                    <div
                      key={message.id}
                      data-rt-msg={message.id}
                      className={cn("rt-m", `rt-m--${message.who}`, {
                        "rt-m--guided": guidedShell && message.kind !== "final",
                        "rt-m--final": message.kind === "final",
                        "is-stale":
                          Boolean(message.stale) || (Boolean(message.done) && INTERACTIVE_KINDS.has(message.kind)),
                      })}
                    >
                      <span className={cn("rt-m__av", { "rt-m__av--me": message.who === "me" })}>
                        {message.who === "ai" ? "QS" : <i />}
                      </span>
                      {guidedShell ? (
                        bubble
                      ) : (
                        <div
                          className={cn("rt-m__b", "rt-m__b--enter", {
                            "is-done": message.done && message.kind !== "progress",
                            "rt-m__b--prog": message.kind === "progress",
                            "rt-m__b--stream": message.id === typingId && !(message.text && message.text.length > 0),
                            // Only the actively typing bubble — not every AI text while busy.
                            "rt-m__b--typing": message.id === typingId,
                          })}
                        >
                          {bubble}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="rt-comp">
            <button
              type="button"
              className="rt-comp__clip"
              aria-label="Attach file"
              disabled={locked}
              onClick={() => fileRef.current?.click()}
            >
              +
            </button>
            <textarea
              ref={composerRef}
              rows={3}
              value={draft}
              placeholder="Answer in your own words, or paste more rates…"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendText();
                }
              }}
            />
            <button
              type="button"
              className={cn("pcbtn pcbtn--main", { "is-busy": locked })}
              disabled={locked}
              onMouseDown={(event) => event.preventDefault()}
              onClick={sendText}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatHumanDate = (iso: string): string =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

type GuidedBlockProps = {
  title: string;
  hint?: string;
  locked?: boolean;
  status?: string;
  /** Decided value for locked cards (carrier name, date range, …). */
  decided?: string;
  /** Optional row above the Apply/Skip bar (e.g. Use Carrier TBN) — inside the card. */
  extra?: ReactNode;
  /** Apply | Skip | Skip all — rendered inside the bordered card in `.rt-guided__actions`. */
  actions?: ReactNode;
  children?: ReactNode;
  id?: string;
  className?: string;
};

/** Shared chrome for validity / carrier / ports / amount / transit / equipment (and locked). */
const GuidedBlock = ({
  title,
  hint,
  locked,
  status,
  decided,
  extra,
  actions,
  children,
  id,
  className,
}: GuidedBlockProps) => (
  <div id={id} className={cn("rt-guided-shell", { "is-locked": locked })}>
    <div className={cn("rt-guided", "rt-guided--enter", className, { "is-locked": locked })}>
      <div className="rt-guided__hd">
        <b>{title}</b>
        {!locked && hint ? <em>{hint}</em> : null}
      </div>
      <div className="rt-guided__body">
        {locked ? (
          <div className="rt-guided__status" aria-live="polite">
            {lockedStatusLine(title.replace(/^Clarifying · /, ""), decided, status)}
          </div>
        ) : (
          children
        )}
      </div>
      {!locked && extra ? <div className="rt-guided__extra">{extra}</div> : null}
      {!locked && actions ? (
        <div className="rt-guided__actions">
          <div className="rt-guided__bar">{actions}</div>
        </div>
      ) : null}
    </div>
  </div>
);

const GuidedActionBar = ({
  canApply,
  onApply,
  onSkip,
  onSkipAll,
}: {
  canApply: boolean;
  onApply: () => void;
  onSkip?: () => void;
  onSkipAll?: () => void;
}) => (
  <>
    <div className="rt-guided__bar-start">
      <button type="button" className="pcbtn pcbtn--main" disabled={!canApply} onClick={onApply}>
        Apply
      </button>
    </div>
    <div className="rt-guided__bar-end">
      {onSkip ? (
        <button type="button" className="pcbtn" onClick={onSkip}>
          Skip
        </button>
      ) : null}
      {onSkipAll ? (
        <button type="button" className="pcbtn" onClick={onSkipAll}>
          Skip all
        </button>
      ) : null}
    </div>
  </>
);

const DateFollowUp = ({
  label,
  hint,
  locked,
  status,
  decided,
  onApply,
  onSkip,
  onSkipAll,
}: {
  label: string;
  hint?: string;
  locked?: boolean;
  status?: string;
  decided?: string;
  onApply: (label: string, isoRange: string) => void;
  onSkip?: () => void;
  onSkipAll?: () => void;
}) => {
  const [from, setFrom] = useState("");
  const [until, setUntil] = useState("");
  const bothSet = Boolean(from && until);
  const invalidRange = bothSet && until < from;

  const fieldTitle = "Validity";
  const title = locked ? fieldTitle : clarifyingTitle("validity");
  // Avoid duplicating the ask bubble / header — only show lead when it adds new detail.
  const genericLabel = !label.trim() || /^from\s*\/?\s*until/i.test(label.trim());
  const apply = () => {
    if (!bothSet || invalidRange) return;
    onApply(`${formatHumanDate(from)} – ${formatHumanDate(until)}`, `${from} to ${until}`);
  };

  return (
    <GuidedBlock
      title={title}
      hint={hint}
      locked={locked}
      status={status ?? "done"}
      decided={decided}
      actions={
        <GuidedActionBar canApply={bothSet && !invalidRange} onApply={apply} onSkip={onSkip} onSkipAll={onSkipAll} />
      }
    >
      {!genericLabel ? <p className="rt-guided__lead">{label}</p> : null}
      <ValidityDateRangeFields
        from={from}
        until={until}
        size="sm"
        onChange={(nextFrom, nextUntil) => {
          setFrom(nextFrom);
          setUntil(nextUntil);
        }}
      />
      {invalidRange && <div className="rt-q__err">Until must be on or after From.</div>}
    </GuidedBlock>
  );
};

/**
 * Guided UX: chips when options.length <= this (enum-like fields);
 * Select when larger. Carrier always Select + searchable regardless.
 */
const GUIDED_SELECT_SEARCH_MIN = 12;
const guidedUsesChips = (optionsLength: number): boolean => optionsLength <= GUIDED_SELECT_SEARCH_MIN;

/** Align with RateCard edit currencies; RUB for common screenshots. */
const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "CNY", "AED", "JPY", "RUB"] as const;

const normalizeGuidedEquipment = (value: string): string => {
  const text = value.trim();
  if (!text || text === "—" || text === "-" || /^not stated$/i.test(text)) return "";
  const compact = text.replace(/[\s'′]/g, "").toUpperCase();
  const match = EQUIPMENT.find((item) => item.replace(/'/g, "").toUpperCase() === compact);
  return match ?? text;
};

const carrierLaneLabel = (rate: RateRecord | undefined, rateId: number): string => {
  if (!rate) return `#${rateId}`;
  const origin = (rate.origin ?? "").trim();
  const destination = (rate.destination ?? "").trim();
  const equipment = (rate.equipment ?? "").trim();
  if (origin && destination && origin !== "—" && destination !== "—") {
    const eq = equipment && equipment !== "—" ? ` · ${equipment}` : "";
    return `${origin}→${destination}${eq}`;
  }
  if (equipment && equipment !== "—") return `${equipment} · #${rateId}`;
  return `#${rateId}`;
};

const GuidedInput = ({
  field,
  label,
  hint,
  locked,
  status,
  decided,
  rateIds,
  rates,
  onApply,
  onSkip,
  onSkipAll,
}: {
  field: GuidedField;
  label: string;
  hint?: string;
  locked?: boolean;
  status?: string;
  decided?: string;
  rateIds?: number[];
  rates?: RateRecord[];
  onApply: (label: string, payload: string) => void;
  onSkip?: () => void;
  onSkipAll?: () => void;
}) => {
  const [carrier, setCarrier] = useState("");
  const [carriersByRate, setCarriersByRate] = useState<Record<string, string>>({});
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<(typeof CURRENCY_OPTIONS)[number]>("USD");
  const [transit, setTransit] = useState("");
  const [equipment, setEquipment] = useState("");

  const multiCarrierIds = field === "carrier" && rateIds && rateIds.length >= 2 && rateIds.length <= 3 ? rateIds : null;
  const ratesById = new Map((rates ?? []).map((rate) => [String(rate.id), rate]));

  // Prefill amount/currency when the rate already has values (e.g. Charges is only THC unclear).
  useEffect(() => {
    if (field !== "amount" || !rateIds?.length || !rates?.length) return;
    const byId = new Map(rates.map((rate) => [String(rate.id), rate]));
    const match = rateIds
      .map((id) => byId.get(String(id)))
      .find((rate) => rate != null && rate.amount != null && Number(rate.amount) > 0);
    if (!match) return;
    setAmount(String(match.amount));
    const code = (match.currency ?? "").trim().toUpperCase();
    if ((CURRENCY_OPTIONS as readonly string[]).includes(code)) {
      setCurrency(code as (typeof CURRENCY_OPTIONS)[number]);
    }
  }, [field, rateIds, rates]);

  if (field === "validity") {
    return (
      <DateFollowUp
        label={label}
        hint={hint}
        locked={locked}
        status={status}
        decided={decided}
        onApply={onApply}
        onSkip={onSkip}
        onSkipAll={onSkipAll}
      />
    );
  }

  const fieldTitle = guidedFieldLabel(field);
  const title = locked ? fieldTitle : clarifyingTitle(field);

  const applyCarrier = () => {
    if (multiCarrierIds) {
      const filled = multiCarrierIds
        .map((id) => {
          const name = (carriersByRate[String(id)] ?? "").trim();
          return name ? { id, name } : null;
        })
        .filter((row): row is { id: number; name: string } => row !== null);
      if (filled.length === 0) return;
      const summary = filled.map((row) => `${row.name} (#${row.id})`).join(", ");
      const payload = `carriers:${filled.map((row) => `${row.id}=${row.name}`).join(";")}`;
      onApply(`Carrier: ${summary}`, payload);
      return;
    }
    const value = carrier.trim();
    if (!value) return;
    onApply(`Carrier: ${value}`, `carrier:${value}`);
  };

  /** Canonical placeholder when carrier is not known yet. */
  const applyCarrierTbn = () => {
    if (multiCarrierIds) {
      const filled = multiCarrierIds.map((id) => {
        const typed = (carriersByRate[String(id)] ?? "").trim();
        return { id, name: typed || CARRIER_TBN };
      });
      const summary = filled.map((row) => `${row.name} (#${row.id})`).join(", ");
      const payload = `carriers:${filled.map((row) => `${row.id}=${row.name}`).join(";")}`;
      onApply(`Carrier: ${summary}`, payload);
      return;
    }
    onApply(`Carrier: ${CARRIER_TBN}`, `carrier:${CARRIER_TBN}`);
  };

  const applyPorts = () => {
    const from = origin.trim();
    const to = destination.trim();
    if (!from || !to) return;
    onApply(`${from} → ${to}`, `origin:${from}|destination:${to}`);
  };

  const applyAmount = () => {
    const raw = amount.trim().replace(",", "");
    const num = Number(raw);
    if (!raw || Number.isNaN(num) || num <= 0) return;
    onApply(`${currency} ${num}`, `amount:${num}|currency:${currency}`);
  };

  const applyTransit = () => {
    const days = Number(transit.trim());
    if (!transit.trim() || Number.isNaN(days) || days < 1) return;
    onApply(days === 1 ? "1 day" : `${days} days`, `transit:${days}`);
  };

  const applyEquipment = () => {
    const value = normalizeGuidedEquipment(equipment);
    if (!value) return;
    onApply(`Equipment: ${value}`, `equipment:${value}`);
  };

  const canApply =
    field === "carrier"
      ? multiCarrierIds
        ? multiCarrierIds.some((id) => Boolean((carriersByRate[String(id)] ?? "").trim()))
        : Boolean(carrier.trim())
      : field === "ports"
        ? Boolean(origin.trim() && destination.trim())
        : field === "amount"
          ? Boolean(amount.trim() && Number(amount) > 0)
          : field === "transit"
            ? transit.trim() !== "" && Number(transit) >= 1
            : Boolean(equipment.trim());

  const runApply =
    field === "carrier"
      ? applyCarrier
      : field === "ports"
        ? applyPorts
        : field === "amount"
          ? applyAmount
          : field === "transit"
            ? applyTransit
            : applyEquipment;

  const carrierExtra =
    field === "carrier" ? (
      <div className="rt-guided__extra-row">
        <button type="button" className="pcbtn" onClick={applyCarrierTbn}>
          {multiCarrierIds ? "Use Carrier TBN for empty" : "Use Carrier TBN"}
        </button>
        <span className="rt-q__hint">
          {multiCarrierIds
            ? "To be nominated — fills empty rows with Carrier TBN."
            : "To be nominated — when carrier is not known yet."}
        </span>
      </div>
    ) : undefined;

  return (
    <GuidedBlock
      title={title}
      hint={hint}
      locked={locked}
      status={status ?? "done"}
      decided={decided}
      extra={carrierExtra}
      actions={<GuidedActionBar canApply={canApply} onApply={runApply} onSkip={onSkip} onSkipAll={onSkipAll} />}
    >
      {label && label !== fieldTitle && label !== title ? <p className="rt-guided__lead">{label}</p> : null}
      {field === "carrier" && multiCarrierIds && (
        <div className="rt-q rt-q--guided rt-q--carrier-stack">
          {multiCarrierIds.map((id) => {
            const name = carriersByRate[String(id)] ?? "";
            const selectValue = name.trim() ? resolveCarrier(name).code : "";
            return (
              <label key={id} className="rt-q__field rt-q__field--grow">
                <span>{carrierLaneLabel(ratesById.get(String(id)), id)}</span>
                <Select
                  searchable
                  ariaLabel={`Carrier for rate ${id}`}
                  placeholder="Select carrier"
                  value={selectValue}
                  options={CARRIER_SELECT_OPTIONS}
                  onChange={(code) => {
                    const resolved = resolveCarrier(code);
                    setCarriersByRate((prev) => ({ ...prev, [String(id)]: resolved.name }));
                  }}
                />
              </label>
            );
          })}
        </div>
      )}
      {field === "carrier" && !multiCarrierIds && (
        <div className="rt-q rt-q--guided">
          <label className="rt-q__field rt-q__field--grow">
            <span>Carrier</span>
            <Select
              searchable
              ariaLabel="Carrier"
              placeholder="Select carrier"
              value={carrier.trim() ? resolveCarrier(carrier).code : ""}
              options={CARRIER_SELECT_OPTIONS}
              onChange={(code) => {
                const resolved = resolveCarrier(code);
                setCarrier(resolved.name);
              }}
            />
          </label>
        </div>
      )}
      {field === "ports" && (
        <div className="rt-q rt-q--guided">
          <label className="rt-q__field rt-q__field--grow">
            <span>Origin</span>
            <input
              className="rt-q__in"
              type="text"
              placeholder="Origin port / city"
              value={origin}
              onChange={(event) => setOrigin(event.target.value)}
            />
          </label>
          <label className="rt-q__field rt-q__field--grow">
            <span>Destination</span>
            <input
              className="rt-q__in"
              type="text"
              placeholder="Destination port / city"
              value={destination}
              onChange={(event) => setDestination(event.target.value)}
            />
          </label>
        </div>
      )}
      {field === "amount" && (
        <div className="rt-q rt-q--guided rt-q--amount">
          <label className="rt-q__field">
            <span>Amount</span>
            <input
              className="rt-q__in"
              type="number"
              min={0}
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </label>
          <label className="rt-q__field">
            <span>Currency</span>
            <Select
              ariaLabel="Currency"
              value={currency}
              options={CURRENCY_OPTIONS.map((code) => ({ value: code, label: code }))}
              onChange={(code) => {
                if ((CURRENCY_OPTIONS as readonly string[]).includes(code)) {
                  setCurrency(code as (typeof CURRENCY_OPTIONS)[number]);
                }
              }}
            />
          </label>
        </div>
      )}
      {field === "transit" && (
        <div className="rt-q rt-q--guided">
          <label className="rt-q__field">
            <span>Transit days</span>
            <input
              className="rt-q__in"
              type="number"
              min={1}
              step={1}
              placeholder="e.g. 21"
              value={transit}
              onChange={(event) => setTransit(event.target.value)}
            />
          </label>
        </div>
      )}
      {field === "equipment" && (
        <div className="rt-q rt-q--guided">
          {guidedUsesChips(EQUIPMENT.length) ? (
            <div className="rt-q__field rt-q__field--grow rt-q__field--chips">
              <span>Equipment</span>
              <div className="rt-q__chips" role="group" aria-label="Equipment">
                {EQUIPMENT.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={cn("rt-q__chip", { "is-on": equipment === item })}
                    aria-pressed={equipment === item}
                    onClick={() => setEquipment(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </GuidedBlock>
  );
};
